import React, { useState } from 'react';
import { Plus, X, Palette } from 'lucide-react';

/**
 * Editor for a product's color variants.
 *
 * Canonical value shape (matches backend `inventory_import._save_product`):
 *   [{ name: "Royal Blue", hex_code: "#1F3A93" }, ...]
 *
 * Props:
 *   value: Array<{name: string, hex_code: string}>
 *   onChange(nextValue): called whenever variants change
 *   helperText?: optional override for the helper line under the editor
 */
const ColorVariantsEditor = ({ value, onChange, helperText }) => {
  const variants = Array.isArray(value) ? value : [];
  const [draftName, setDraftName] = useState('');
  const [draftHex, setDraftHex] = useState('#1F3A93');

  const addVariant = () => {
    const name = draftName.trim();
    if (!name && !draftHex) return;
    // De-dupe by hex_code (case-insensitive). Keeps the first one.
    const hex = draftHex.toLowerCase();
    if (variants.some(v => (v.hex_code || '').toLowerCase() === hex)) {
      // Update name on the existing entry instead of duplicating
      const next = variants.map(v =>
        (v.hex_code || '').toLowerCase() === hex ? { ...v, name: name || v.name } : v
      );
      onChange(next);
    } else {
      onChange([...variants, { name: name || hex.toUpperCase(), hex_code: draftHex }]);
    }
    setDraftName('');
  };

  const removeAt = (idx) => {
    const next = variants.filter((_, i) => i !== idx);
    onChange(next);
  };

  const updateNameAt = (idx, name) => {
    const next = variants.map((v, i) => (i === idx ? { ...v, name } : v));
    onChange(next);
  };

  const updateHexAt = (idx, hex_code) => {
    const next = variants.map((v, i) => (i === idx ? { ...v, hex_code } : v));
    onChange(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addVariant();
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 space-y-3" data-testid="color-variants-editor">
      <div className="flex items-center gap-2">
        <Palette className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-700">Color Variants</span>
        <span className="text-xs text-slate-400">
          {variants.length === 0 ? 'None added' : `${variants.length} added`}
        </span>
      </div>

      {/* Existing chips */}
      {variants.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="color-variants-list">
          {variants.map((v, idx) => (
            <div
              key={`${v.hex_code}-${idx}`}
              className="group inline-flex items-center gap-1.5 pl-1 pr-1.5 py-1 bg-slate-50 border border-slate-200 rounded-full hover:border-slate-300 transition-colors"
              data-testid={`color-variant-chip-${idx}`}
            >
              <input
                type="color"
                value={v.hex_code || '#000000'}
                onChange={(e) => updateHexAt(idx, e.target.value)}
                className="w-6 h-6 rounded-full border border-white shadow-sm cursor-pointer p-0 bg-transparent"
                aria-label={`Edit hex for ${v.name || 'color'}`}
                data-testid={`color-variant-hex-${idx}`}
              />
              <input
                type="text"
                value={v.name || ''}
                onChange={(e) => updateNameAt(idx, e.target.value)}
                placeholder="Name"
                maxLength={50}
                className="bg-transparent text-xs font-medium text-slate-800 outline-none w-20 sm:w-24"
                data-testid={`color-variant-name-${idx}`}
              />
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="p-0.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                aria-label={`Remove ${v.name || 'color'}`}
                data-testid={`color-variant-remove-${idx}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add new variant row */}
      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
        <input
          type="color"
          value={draftHex}
          onChange={(e) => setDraftHex(e.target.value)}
          className="w-9 h-9 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white"
          aria-label="Pick color"
          data-testid="color-variants-draft-hex"
        />
        <input
          type="text"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Color name (e.g. Royal Blue)"
          maxLength={50}
          className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          data-testid="color-variants-draft-name"
        />
        <button
          type="button"
          onClick={addVariant}
          className="inline-flex items-center gap-1 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition-colors"
          data-testid="color-variants-add-btn"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      <p className="text-xs text-slate-500">
        {helperText ||
          'Add the color options this product is available in. Required to enable AI Color Change (≥ 2 colors).'}
      </p>
    </div>
  );
};

/**
 * Compact read-only swatch row used in product lists / cards.
 * Shows up to `max` swatches; collapses overflow to "+N".
 */
export const ColorVariantsSwatchRow = ({ variants, max = 5, size = 'sm' }) => {
  const list = Array.isArray(variants) ? variants.filter(v => v && v.hex_code) : [];
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  const cls = size === 'xs' ? 'w-3 h-3' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-1" data-testid="color-variants-swatch-row">
      {shown.map((v, i) => (
        <span
          key={`${v.hex_code}-${i}`}
          title={v.name || v.hex_code}
          className={`${cls} rounded-full border border-white ring-1 ring-slate-200 inline-block`}
          style={{ backgroundColor: v.hex_code }}
        />
      ))}
      {extra > 0 && (
        <span className="text-[10px] font-medium text-slate-500 ml-0.5">+{extra}</span>
      )}
    </div>
  );
};

export default ColorVariantsEditor;
